import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Modal, Form, Input, Select, Button, Space, Tag, Popconfirm, message, Spin, Checkbox,
} from 'antd';
import DraggableModal from '../../components/DraggableModal';
import {
  PlusOutlined, SearchOutlined, UploadOutlined, DownloadOutlined,
  SortAscendingOutlined, HolderOutlined, PlusCircleOutlined,
} from '@ant-design/icons';
import api from '../../api';
import { phoneRules, workNoRules, chineseNameRules } from '../../userFieldRules';

const roleMap = { 1: '普通用户', 2: '区域管理员', 3: '超级管理员' };
const normalizeRole = (v) => {
  const n = Number(v);
  if (n >= 3) return 3;
  if (n === 2) return 2;
  return 1;
};
const getCategoryById = (categories, id) => (categories || []).find(c => c.id === id);
const getCategoryIdByName = (categories, name) => (categories || []).find(c => c.name === name)?.id;
const getIdentityLabel = (record) => record?.permission_category_name || roleMap[normalizeRole(record?.role)] || '普通用户';
const getIdentityColor = (record) => {
  const label = getIdentityLabel(record);
  if (label === '超级管理员') return 'purple';
  if (label === '区域管理员') return 'blue';
  return 'default';
};

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), 'zh-Hans-CN', {
  numeric: true,
  sensitivity: 'base',
});

const compareByRule = (left, right, field, order) => {
  let result = 0;
  if (field === 'role' || field === 'status') {
    result = Number(left?.[field] ?? 0) - Number(right?.[field] ?? 0);
  } else if (field === 'create_time') {
    result = new Date(left?.create_time || 0).getTime() - new Date(right?.create_time || 0).getTime();
  } else {
    result = compareText(left?.[field], right?.[field]);
  }
  return order === 'desc' ? -result : result;
};

const sortRowsByRules = (rows, rules) => {
  const validRules = (rules || []).filter(rule => rule?.field && rule?.order);
  if (!validRules.length) return rows;
  return [...rows].sort((a, b) => {
    for (let i = 0; i < validRules.length; i += 1) {
      const { field, order } = validRules[i];
      const compared = compareByRule(a, b, field, order);
      if (compared !== 0) return compared;
    }
    return 0;
  });
};

export default function Users() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchName, setSearchName] = useState('');
  const [filterPermissionCategoryId, setFilterPermissionCategoryId] = useState(undefined);
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [deleteFilterForm] = Form.useForm();
  const [deleteFilterModalOpen, setDeleteFilterModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // 删除用户时：如果存在关联资产，弹窗引导变更资产信息
  const [assetChangeOpen, setAssetChangeOpen] = useState(false);
  const [assetChangeLoading, setAssetChangeLoading] = useState(false);
  const [assetChangeUser, setAssetChangeUser] = useState(null); // 待删除用户
  const [assetChangeForceSuperAdmin, setAssetChangeForceSuperAdmin] = useState(false);
  const [assetChangeAssets, setAssetChangeAssets] = useState([]);
  const [assetChangeIndex, setAssetChangeIndex] = useState(0);
  const [assetChangeUsersOpt, setAssetChangeUsersOpt] = useState([]); // {id, username}
  const [assetChangeLocsOpt, setAssetChangeLocsOpt] = useState([]); // {id, location_name, region_name}
  const [assetNewUserId, setAssetNewUserId] = useState(null);
  const [assetNewLocationId, setAssetNewLocationId] = useState(null);

  const [assetBatchOpen, setAssetBatchOpen] = useState(false);
  const [assetBatchLoading, setAssetBatchLoading] = useState(false);
  const [assetBatchNewUserId, setAssetBatchNewUserId] = useState(null);
  const [assetBatchNewLocationId, setAssetBatchNewLocationId] = useState(null);

  const assetWizardResolveRef = useRef(null); // Promise resolve for wizard finish
  /** 批量删除时：是否对其余「有关联资产」的用户沿用首次选择（不再逐个弹窗） */
  const batchAssetPolicyRef = useRef({ unifyNext: false, decision: null });
  const defaultSortRules = [
    { field: 'username', order: 'asc' },
    { field: 'work_no', order: 'asc' },
    { field: 'phone', order: 'asc' },
    { field: 'create_time', order: 'desc' },
  ];
  const [sortRules, setSortRules] = useState(defaultSortRules);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [draftSortRules, setDraftSortRules] = useState([]);
  const [draggingSortIndex, setDraggingSortIndex] = useState(null);
  const [opLogOpen, setOpLogOpen] = useState(false);
  const [opLogs, setOpLogs] = useState([]);
  const [opLogLoading, setOpLogLoading] = useState(false);
  const [opLogSelected, setOpLogSelected] = useState([]);
  const [ioDialog, setIoDialog] = useState({
    open: false, loading: false, title: '', lines: [],
  });

  const sortFieldOptions = [
    { value: 'username', label: '姓名' },
    { value: 'work_no', label: '工号' },
    { value: 'phone', label: '手机号' },
    { value: 'role', label: '角色类别' },
    { value: 'status', label: '状态' },
    { value: 'create_time', label: '创建时间' },
  ];
  const ordinaryCategoryId = getCategoryIdByName(permissionCategories, '普通用户');

  const buildParams = useCallback((page = 1, pageSize = 10, rulesOverride) => {
    const rules = rulesOverride !== undefined ? rulesOverride : sortRules;
    const params = { page, page_size: pageSize };
    if (searchName && searchName.trim()) params.search_name = searchName.trim();
    if (filterPermissionCategoryId !== undefined) params.permission_category_id = filterPermissionCategoryId;
    if (filterStatus !== undefined) params.status = filterStatus;
    const validSort = rules
      .filter(rule => rule?.field && rule?.order)
      .map(rule => `${rule.field}:${rule.order}`);
    if (validSort.length) params.sort_by = validSort.join(',');
    return params;
  }, [sortRules, searchName, filterPermissionCategoryId, filterStatus]);

  const fetchData = useCallback(async (page = 1, pageSize = 10, rulesOverride) => {
    setLoading(true);
    try {
      const params = buildParams(page, pageSize, rulesOverride);
      const res = await api.get('/users', { params });
      const rows = Array.isArray(res?.items) ? res.items : [];
      setData(sortRowsByRules(rows, params.sort_by ? rulesOverride || sortRules : sortRules));
      setPagination(p => ({ ...p, current: page, pageSize, total: res?.total || 0 }));
      setSelectedRowKeys([]);
      setSelectedRows([]);
    } catch { message.error('获取用户列表失败'); }
    setLoading(false);
  }, [buildParams, sortRules]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** 按当前搜索/筛选/排序条件拉取全部用户（用于「勾选全部」） */
  const fetchAllUsersMatchingFilters = useCallback(async () => {
    const pageSize = 100;
    let page = 1;
    const all = [];
    const validSort = sortRules
      .filter((rule) => rule?.field && rule?.order)
      .map((rule) => `${rule.field}:${rule.order}`);
    const sortBy = validSort.length ? validSort.join(',') : '';
    while (true) {
      const params = { page, page_size: pageSize };
      if (searchName && searchName.trim()) params.search_name = searchName.trim();
      if (filterPermissionCategoryId !== undefined) {
        params.permission_category_id = filterPermissionCategoryId;
      }
      if (filterStatus !== undefined) params.status = filterStatus;
      if (sortBy) params.sort_by = sortBy;
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get('/users', { params });
      const items = Array.isArray(res?.items) ? res.items : [];
      all.push(...items);
      const total = Number(res?.total || 0);
      if (!total || all.length >= total || !items.length) break;
      page += 1;
    }
    return sortRowsByRules(all, sortRules);
  }, [searchName, filterPermissionCategoryId, filterStatus, sortRules]);

  const fetchPermissionCategories = useCallback(async () => {
    try {
      const res = await api.get('/permission-categories');
      setPermissionCategories(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setPermissionCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchPermissionCategories();
  }, [fetchPermissionCategories]);

  const handleSearch = () => fetchData(1, pagination.pageSize);

  const handleReset = () => {
    setSearchName('');
    setFilterPermissionCategoryId(undefined);
    setFilterStatus(undefined);
  };

  const handleCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      permission_category_id: ordinaryCategoryId,
      status: 1,
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      username: record.username,
      phone: record.phone,
      work_no: record.work_no,
      permission_category_id: record.permission_category_id ?? ordinaryCategoryId,
      status: record.status,
      password: undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (payload.permission_category_id == null && ordinaryCategoryId != null) {
        payload.permission_category_id = ordinaryCategoryId;
      }
      const selectedCategory = getCategoryById(permissionCategories, payload.permission_category_id);
      const isSuperAdminCategory = selectedCategory?.name === '超级管理员';
      payload.role = isSuperAdminCategory ? 3 : ((editing?.region_ids?.length || 0) > 0 ? 2 : 1);
      if (editing) {
        if (!payload.password || !String(payload.password).trim()) {
          delete payload.password;
        }
        await api.put(`/users/${editing.id}`, payload);
        message.success('更新成功');
      } else {
        await api.post('/users', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch { /* validation or api error */ }
  };

  const handleDelete = async (id, forceSuperAdmin = false) => {
    try {
      const res = await api.post('/users/delete-batch', {
        scope: 'ids',
        ids: [id],
        force_super_admin: forceSuperAdmin,
      });
      message.success(`删除成功（${res?.deleted_count || 0} 条）`);
      fetchData(pagination.current, pagination.pageSize);
      return true;
    } catch (err) {
      message.error(err.message || '删除失败');
      return false;
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的用户');
      return;
    }
    if (selectedRows.length !== selectedRowKeys.length) {
      message.warning('勾选数据未完全加载，请切换分页勾选或重新使用「勾选全部」');
      return;
    }
    batchAssetPolicyRef.current = { unifyNext: false, decision: null };
    const toDelete = [...selectedRows];
    let okCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const r of toDelete) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await deleteUserWithAssetHandling(
        r,
        normalizeRole(r.role) === 3,
        batchAssetPolicyRef,
      );
      if (ok) okCount += 1;
    }
    batchAssetPolicyRef.current = { unifyNext: false, decision: null };
    message.success(`处理完成：成功删除 ${okCount} 个用户`);
    fetchData(pagination.current, pagination.pageSize);
  };

  const fetchAssetsByUser = async (userId) => {
    const pageSize = 100; // 后端约束：page_size <= 100
    let page = 1;
    const all = [];
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get('/assets', {
        params: { user_id: userId, page, page_size: pageSize },
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      all.push(...items);
      const total = Number(res?.total || 0);
      if (!total || all.length >= total || !items.length) break;
      page += 1;
    }
    return all;
  };

  const fetchAssetFilterOptions = async () => {
    const res = await api.get('/assets/filter-options');
    // 注意：api 拦截器会直接返回 FastAPI 的 body.data，因此这里无需再取 res.data
    const data = res || {};
    return {
      users: Array.isArray(data?.asset_users) ? data.asset_users : [],
      locations: Array.isArray(data?.locations) ? data.locations : [],
    };
  };

  const openAssetChangeWizard = async (record, forceSuperAdmin, assets, resolve) => {
    assetWizardResolveRef.current = resolve;
    setAssetChangeUser(record);
    setAssetChangeForceSuperAdmin(forceSuperAdmin);
    setAssetChangeAssets(assets);
    setAssetChangeIndex(0);
    setAssetNewUserId(null);
    setAssetNewLocationId(null);
    setAssetBatchNewUserId(null);
    setAssetBatchNewLocationId(null);
    setAssetBatchOpen(false);
    setAssetChangeOpen(true);

    try {
      const { users, locations } = await fetchAssetFilterOptions();
      const filteredUsers = users.filter(u => u?.id !== record?.id);
      setAssetChangeUsersOpt(filteredUsers);
      setAssetChangeLocsOpt(locations);
    } catch (e) {
      message.error(e?.message || '加载资产变更选项失败');
      setAssetChangeOpen(false);
      assetWizardResolveRef.current?.(false);
      assetWizardResolveRef.current = null;
    }
  };

  const closeAssetWizard = (ok) => {
    setAssetBatchOpen(false);
    setAssetChangeOpen(false);
    if (!ok) {
      batchAssetPolicyRef.current = { unifyNext: false, decision: null };
    }
    if (assetWizardResolveRef.current) {
      assetWizardResolveRef.current(ok);
      assetWizardResolveRef.current = null;
    }
  };

  const applyAssetChangeForCurrent = async () => {
    const assets = assetChangeAssets || [];
    const idx = assetChangeIndex || 0;
    const asset = assets[idx];
    if (!asset) return;
    if (!assetNewUserId || !assetNewLocationId) {
      message.warning('请选择新使用人和新存放地点');
      return;
    }

    setAssetChangeLoading(true);
    try {
      await api.put(`/assets/${asset.id}/change`, {
        new_user_id: assetNewUserId,
        new_location_id: assetNewLocationId,
      });
      setAssetChangeLoading(false);

      if (idx >= assets.length - 1) {
        const ok = await handleDelete(assetChangeUser.id, assetChangeForceSuperAdmin);
        closeAssetWizard(ok);
      } else {
        setAssetChangeIndex(idx + 1);
      }
    } catch (e) {
      setAssetChangeLoading(false);
      message.error(e?.message || '资产变更失败');
    }
  };

  const applyAssetBatchChangeAndDelete = async () => {
    const assets = assetChangeAssets || [];
    if (!assetBatchNewUserId || !assetBatchNewLocationId) {
      message.warning('请选择一键变更的使用人和存放地点');
      return;
    }
    setAssetBatchLoading(true);
    try {
      // 逐条更新，避免一次请求过多导致超时
      // eslint-disable-next-line no-restricted-syntax
      for (const a of assets) {
        await api.put(`/assets/${a.id}/change`, {
          new_user_id: assetBatchNewUserId,
          new_location_id: assetBatchNewLocationId,
        });
      }
      setAssetBatchLoading(false);
      const ok = await handleDelete(assetChangeUser.id, assetChangeForceSuperAdmin);
      closeAssetWizard(ok);
    } catch (e) {
      setAssetBatchLoading(false);
      message.error(e?.message || '一键变更失败');
    }
  };

  const deleteUserWithAssetHandling = async (record, forceSuperAdminAllowed = false, batchRef = null) => {
    // 超级管理员删除：先二次确认（若取消则直接结束）
    if (normalizeRole(record?.role) === 3 && forceSuperAdminAllowed) {
      const ok = await new Promise(resolve => {
        Modal.confirm({
          title: '删除超级管理员需要二次确认',
          content: '确认后将删除超级管理员账号，可能影响系统功能。',
          okText: '确认',
          cancelText: '取消',
          onOk: () => {
            Modal.confirm({
              title: '再次确认删除',
              content: `确认删除账号：${record.username}（工号：${record.work_no}）？`,
              okText: '删除',
              okButtonProps: { danger: true },
              cancelText: '取消',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          },
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return false;
    }

    const assets = await fetchAssetsByUser(record.id);
    if (!assets.length) {
      return handleDelete(record.id, forceSuperAdminAllowed);
    }

    const policy = batchRef?.current;
    if (policy?.unifyNext && policy.decision === 'skip') {
      return false;
    }
    if (policy?.unifyNext && policy.decision === 'reassign') {
      return new Promise(resolve => {
        openAssetChangeWizard(record, forceSuperAdminAllowed, assets, resolve);
      });
    }

    const unifyPick = { apply: false };
    return new Promise(resolve => {
      Modal.confirm({
        title: '该用户存在关联资产',
        content: (
          <div>
            <p style={{ marginBottom: 12 }}>
              是否现在变更资产信息？变更后将自动删除该用户。
            </p>
            {batchRef ? (
              <Checkbox
                onChange={(e) => { unifyPick.apply = e.target.checked; }}
              >
                对列表中其余有关联资产的用户执行相同选择（不再逐个询问）
              </Checkbox>
            ) : null}
          </div>
        ),
        okText: '变更并删除',
        okButtonProps: { danger: true },
        cancelText: '取消删除',
        onOk: () => {
          if (batchRef && unifyPick.apply) {
            batchRef.current = { unifyNext: true, decision: 'reassign' };
          }
          openAssetChangeWizard(record, forceSuperAdminAllowed, assets, resolve);
        },
        onCancel: () => {
          if (batchRef && unifyPick.apply) {
            batchRef.current = { unifyNext: true, decision: 'skip' };
          }
          resolve(false);
        },
      });
    });
  };

  useEffect(() => {
    if (!assetChangeOpen) return;
    const asset = assetChangeAssets?.[assetChangeIndex];
    if (!asset || !assetChangeUser) return;
    let nextUserId = asset.user_id;
    if (nextUserId === assetChangeUser.id) {
      const other = assetChangeUsersOpt.find(u => u?.id !== assetChangeUser.id);
      nextUserId = other?.id ?? null;
    }
    setAssetNewUserId(nextUserId);
    setAssetNewLocationId(asset.location_id ?? null);
  }, [assetChangeOpen, assetChangeIndex, assetChangeAssets, assetChangeUser, assetChangeUsersOpt]);

  const handleDeleteByFilter = async () => {
    try {
      const values = await deleteFilterForm.validateFields();
      const hasAny = [values.keyword, values.permission_category_id, values.status]
        .some(v => v !== undefined && v !== null && v !== '');
      if (!hasAny) {
        message.warning('请至少填写一项筛选条件，禁止空白删除');
        return;
      }
      const pageSize = 100;
      const matchedUsers = [];
      let page = 1;
      while (true) {
        const params = { page, page_size: pageSize };
        if (values.keyword) params.keyword = values.keyword;
        if (values.permission_category_id !== undefined) params.permission_category_id = values.permission_category_id;
        if (values.status !== undefined) params.status = values.status;
        const res = await api.get('/users', { params });
        const items = Array.isArray(res?.items) ? res.items : [];
        matchedUsers.push(...items);
        const total = Number(res?.total || 0);
        if (!total || matchedUsers.length >= total) break;
        page += 1;
      }

      if (!matchedUsers.length) {
        message.warning('未匹配到用户');
        return;
      }

      setDeleteFilterModalOpen(false);
      batchAssetPolicyRef.current = { unifyNext: false, decision: null };
      let okCount = 0;
      // eslint-disable-next-line no-restricted-syntax
      for (const u of matchedUsers) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await deleteUserWithAssetHandling(
          u,
          normalizeRole(u.role) === 3,
          batchAssetPolicyRef,
        );
        if (ok) okCount += 1;
      }
      batchAssetPolicyRef.current = { unifyNext: false, decision: null };
      message.success(`处理完成：成功删除 ${okCount} 个用户`);
      fetchData(1, pagination.pageSize);
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const openSortModal = () => {
    setDraftSortRules([]);
    setSortModalOpen(true);
  };

  const applySortRules = () => {
    const cleaned = draftSortRules.filter(r => r.field && r.order);
    const fields = cleaned.map(r => r.field);
    if (new Set(fields).size !== fields.length) {
      message.warning('排序字段不能重复，请调整后再应用');
      return;
    }
    const next = cleaned.length ? cleaned : [...defaultSortRules];
    setSortRules(next);
    setSortModalOpen(false);
    fetchData(1, pagination.pageSize, next);
  };

  const restoreDefaultSortRules = () => {
    const next = [...defaultSortRules];
    setSortRules(next);
    setDraftSortRules([]);
    setSortModalOpen(false);
    fetchData(1, pagination.pageSize, next);
  };

  const handleExport = async () => {
    setIoDialog({
      open: true,
      loading: true,
      title: '正在导出用户',
      lines: ['请稍候，导出完成前请勿重复提交。'],
    });
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/asset-users/excel/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('导出失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setIoDialog({
        open: true,
        loading: false,
        title: '导出完成',
        lines: [
          `成功数：${Number(pagination.total || 0)}`,
          '更新数：0',
          '跳过数：0',
        ],
      });
    } catch {
      setIoDialog({
        open: true,
        loading: false,
        title: '导出失败',
        lines: ['导出过程中发生错误，请重试。'],
      });
    }
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/asset-users/excel/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('下载失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '用户导入模板.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('已下载用户导入模板（状态下拉可选）');
    } catch (err) {
      message.error(err.message || '下载模板失败');
    }
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      setIoDialog({
        open: true,
        loading: true,
        title: '正在导入用户',
        lines: ['请稍候，导入完成前请勿重复提交。'],
      });
      try {
        const token = localStorage.getItem('token');
        const fd = new FormData();
        fd.append('file', f);
        const r = await fetch(`${window.location.origin}/api/asset-users/excel/import`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const j = await r.json();
        if (j.code !== 200) throw new Error(j.msg || '导入失败');
        const { created = 0, updated = 0, errors = [] } = j.data || {};
        setIoDialog({
          open: true,
          loading: false,
          title: '导入完成',
          lines: [
            `成功数：${Number(created || 0) + Number(updated || 0)}`,
            `更新数：${updated}`,
            `跳过数：${errors.length}`,
          ],
        });
        fetchData(pagination.current, pagination.pageSize);
      } catch (err) {
        setIoDialog({
          open: true,
          loading: false,
          title: '导入失败',
          lines: [err.message || '导入失败，请重试。'],
        });
      }
    };
    input.click();
  };

  const fetchOpLogs = async () => {
    setOpLogLoading(true);
    try {
      const res = await api.get('/data-op-logs', { params: { module: 'users', page: 1, page_size: 200 } });
      setOpLogs(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      message.error(err.message || '获取操作日志失败');
    }
    setOpLogLoading(false);
  };

  const openOpLogs = () => {
    setOpLogSelected([]);
    setOpLogOpen(true);
    fetchOpLogs();
  };

  const columns = [
    { title: '姓名', dataIndex: 'username', key: 'username' },
    { title: '工号', dataIndex: 'work_no', key: 'work_no' },
    { title: '手机号（登录账号）', dataIndex: 'phone', key: 'phone' },
    {
      title: '角色类别', dataIndex: 'permission_category_name', key: 'permission_category_name',
      render: (_, record) => {
        const label = getIdentityLabel(record);
        return <Tag color={getIdentityColor(record)}>{label}</Tag>;
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: v => <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '启用' : '禁用'}</Tag>,
    },
    { title: '创建时间', dataIndex: 'create_time', key: 'create_time' },
    {
      title: '操作', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" style={{ padding: 0 }} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          {normalizeRole(record.role) === 3 ? (
            <Button
              type="link"
              danger
              style={{ padding: 0 }}
              onClick={() => deleteUserWithAssetHandling(record, true)}
            >
              删除
            </Button>
          ) : (
            <Popconfirm title="确认删除该用户？" onConfirm={() => deleteUserWithAssetHandling(record, false)}>
              <Button type="link" danger style={{ padding: 0 }}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 16, gap: 12, flexWrap: 'wrap',
      }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, flexShrink: 0 }}>用户管理</h1>
        <Space wrap size={[8, 8]}>
          <Button icon={<UploadOutlined />} onClick={handleImportClick}>导入</Button>
          <Button onClick={handleDownloadImportTemplate}>下载导入模板</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
          <Button onClick={openOpLogs}>操作日志</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增用户</Button>
        </Space>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
        <Space size={[8, 8]} wrap style={{ width: '100%' }}>
          <Input
            placeholder="按姓名搜索"
            prefix={<SearchOutlined />}
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            style={{ width: '100%', maxWidth: 280, minWidth: 160 }}
          />
          <Select
            placeholder="全部角色类别"
            value={filterPermissionCategoryId}
            onChange={setFilterPermissionCategoryId}
            allowClear
            style={{ width: 160, minWidth: 140 }}
            options={permissionCategories.map(c => ({ value: c.id, label: c.name }))}
          />
          <Select
            placeholder="全部状态"
            value={filterStatus}
            onChange={setFilterStatus}
            allowClear
            style={{ width: 120 }}
            options={[
              { value: 1, label: '启用' },
              { value: 0, label: '禁用' },
            ]}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
        共 <span style={{ fontSize: 20, fontWeight: 600, color: '#333' }}>{pagination.total}</span> 个用户
        <span style={{ marginLeft: 8, fontSize: 12, color: '#999', display: 'inline' }}>
          表头勾选为当前页；可下拉选「本页」或「全部」。
        </span>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Button icon={<SortAscendingOutlined />} onClick={openSortModal}>
          自定义排序
        </Button>
        <Popconfirm
          title={`仅删除已勾选项：确认删除 ${selectedRowKeys.length} 个用户？`}
          description="此操作只影响当前勾选记录。"
          onConfirm={() => handleDeleteSelected()}
          disabled={!selectedRowKeys.length}
        >
          <Button style={{ marginLeft: 12 }} danger disabled={!selectedRowKeys.length}>删除已勾选</Button>
        </Popconfirm>
        <Button style={{ marginLeft: 8 }} danger onClick={() => { deleteFilterForm.resetFields(); setDeleteFilterModalOpen(true); }}>
          按筛选条件删除
        </Button>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 8 }}>
        <Table
          rowKey="id"
          scroll={{ x: 900 }}
          rowSelection={{
            selectedRowKeys,
            preserveSelectedRowKeys: true,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys);
              setSelectedRows((prev) => {
                const byId = new Map();
                (prev || []).forEach((r) => {
                  if (r?.id != null) byId.set(r.id, r);
                });
                (rows || []).forEach((r) => {
                  if (r?.id != null) byId.set(r.id, r);
                });
                (data || []).forEach((r) => {
                  if (r?.id != null && keys.includes(r.id)) byId.set(r.id, r);
                });
                return keys.map((id) => byId.get(id)).filter(Boolean);
              });
            },
            selections: [
              {
                key: 'page-users',
                text: '勾选本页',
                onSelect: () => {
                  const keys = data.map((r) => r.id);
                  setSelectedRowKeys(keys);
                  setSelectedRows([...data]);
                },
              },
              {
                key: 'all-filtered-users',
                text: '勾选全部（当前筛选/排序下全部用户）',
                onSelect: async () => {
                  const hide = message.loading('正在加载全部用户…', 0);
                  try {
                    const all = await fetchAllUsersMatchingFilters();
                    setSelectedRowKeys(all.map((r) => r.id));
                    setSelectedRows(all);
                    message.success(`已勾选 ${all.length} 个用户`);
                  } catch {
                    message.error('加载用户列表失败');
                  } finally {
                    hide();
                  }
                },
              },
              Table.SELECTION_NONE,
            ],
          }}
          columns={columns}
          dataSource={data}
          loading={loading}
          bordered={false}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => fetchData(page, pageSize),
            showTotal: t => `共 ${t} 条`,
            showSizeChanger: true,
          }}
        />
      </div>

      <DraggableModal
        title={editing ? '编辑用户' : '新增用户'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <>
              <Form.Item
                name="phone"
                label="手机号（登录账号）"
                rules={phoneRules}
                extra="11位数字，凭手机号与密码登录；演示账号初始密码为 123456"
              >
                <Input placeholder="11位数字手机号" maxLength={11} inputMode="numeric" />
              </Form.Item>
              <Form.Item name="username" label="姓名" rules={chineseNameRules}>
                <Input placeholder="2～20个汉字，可含间隔号·" maxLength={20} />
              </Form.Item>
              <Form.Item name="work_no" label="工号" rules={workNoRules}>
                <Input placeholder="6位数字" maxLength={6} inputMode="numeric" />
              </Form.Item>
              <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]} extra="长度与字符类型不限，可含数字、字母、标点">
                <Input.Password placeholder="初始密码可设为 123456" />
              </Form.Item>
              <Form.Item name="permission_category_id" label="角色类别" rules={[{ required: true, message: '请选择角色类别' }]}>
                <Select
                  placeholder="请选择角色类别"
                  options={permissionCategories.map(c => ({ value: c.id, label: c.name }))}
                />
              </Form.Item>
            </>
          )}
          {editing && (
            <>
              <Form.Item name="username" label="姓名" rules={chineseNameRules}>
                <Input placeholder="姓名" maxLength={20} />
              </Form.Item>
              <Form.Item name="phone" label="手机号（登录账号）" rules={phoneRules}>
                <Input placeholder="11位数字" maxLength={11} inputMode="numeric" />
              </Form.Item>
              <Form.Item name="work_no" label="工号" rules={workNoRules}>
                <Input placeholder="6位数字" maxLength={6} inputMode="numeric" />
              </Form.Item>
              <Form.Item name="password" label="新密码" extra="不填则保留原密码；若填写，可为任意字符组合">
                <Input.Password placeholder="留空表示不修改" />
              </Form.Item>
              <Form.Item name="permission_category_id" label="角色类别" rules={[{ required: true, message: '请选择角色类别' }]}>
                <Select
                  placeholder="请选择角色类别"
                  options={permissionCategories.map(c => ({ value: c.id, label: c.name }))}
                />
              </Form.Item>
              <Form.Item name="status" label="状态">
                <Select
                  placeholder="请选择状态"
                  options={[
                    { value: 1, label: '启用' },
                    { value: 0, label: '禁用' },
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </DraggableModal>

      <DraggableModal
        title={ioDialog.title || '处理中'}
        open={ioDialog.open}
        closable={!ioDialog.loading}
        maskClosable={false}
        keyboard={!ioDialog.loading}
        footer={ioDialog.loading ? null : [
          <Button key="ok" type="primary" onClick={() => setIoDialog({
            open: false, loading: false, title: '', lines: [],
          })}
          >
            确定
          </Button>,
        ]}
      >
        {ioDialog.loading ? (
          <div style={{ padding: '8px 0', textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12, color: '#666' }}>{ioDialog.lines?.[0] || '处理中，请稍候...'}</div>
          </div>
        ) : (
          <div style={{ lineHeight: 1.9 }}>
            {(ioDialog.lines || []).map((line, idx) => <div key={`io-line-${idx}`}>{line}</div>)}
          </div>
        )}
      </DraggableModal>

      <DraggableModal
        title="导入导出操作日志"
        open={opLogOpen}
        onCancel={() => setOpLogOpen(false)}
        footer={[
          <Button
            key="exportSelected"
            onClick={async () => {
              if (!opLogSelected.length) { message.warning('请先勾选日志记录'); return; }
              const token = localStorage.getItem('token');
              const r = await fetch(`${window.location.origin}/api/data-op-logs/export-selected-details`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ log_ids: opLogSelected }),
              });
              if (!r.ok) { message.error('导出失败'); return; }
              const blob = await r.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `users_op_logs_${Date.now()}.xlsx`; a.click();
              window.URL.revokeObjectURL(url);
            }}
          >
            导出选中详情
          </Button>,
          <Button key="close" onClick={() => setOpLogOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <Table
          rowKey="id"
          size="small"
          loading={opLogLoading}
          dataSource={opLogs}
          rowSelection={{ selectedRowKeys: opLogSelected, onChange: setOpLogSelected }}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: '时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
            { title: '类型', dataIndex: 'action', key: 'action', width: 80, render: v => (v === 'import' ? '导入' : '导出') },
            { title: '文件', dataIndex: 'file_name', key: 'file_name', width: 200 },
            { title: '总数', dataIndex: 'total_count', key: 'total_count', width: 70 },
            { title: '新增/成功', dataIndex: 'created_count', key: 'created_count', width: 90 },
            { title: '更新', dataIndex: 'updated_count', key: 'updated_count', width: 70 },
            { title: '跳过', dataIndex: 'skipped_count', key: 'skipped_count', width: 70 },
            { title: '操作人', dataIndex: 'operator_name', key: 'operator_name', width: 90 },
            {
              title: '操作',
              key: 'actions',
              render: (_, r) => (
                <Button
                  type="link"
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    const resp = await fetch(`${window.location.origin}/api/data-op-logs/${r.id}/export-details`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!resp.ok) { message.error('导出失败'); return; }
                    const blob = await resp.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `users_op_log_${r.id}.xlsx`; a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  导出详情
                </Button>
              ),
            },
          ]}
        />
      </DraggableModal>

      <DraggableModal
        title="按筛选条件删除用户（需重新填写）"
        open={deleteFilterModalOpen}
        onOk={handleDeleteByFilter}
        onCancel={() => setDeleteFilterModalOpen(false)}
        okText="确认删除"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#cf1322' }}>
          为避免误删，必须重新填写筛选条件；禁止空白条件删除。
        </div>
        <Form form={deleteFilterForm} layout="vertical">
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="姓名/工号/手机号" allowClear />
          </Form.Item>
          <Form.Item name="permission_category_id" label="角色类别">
            <Select
              allowClear
              options={permissionCategories.map(c => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select allowClear options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]} />
          </Form.Item>
        </Form>
      </DraggableModal>

      <DraggableModal
        title="变更资产并删除用户"
        open={assetChangeOpen}
        footer={null}
        onCancel={() => closeAssetWizard(false)}
        maskClosable={false}
        destroyOnClose
      >
        {assetChangeAssets?.length ? (
          <>
            <div style={{ marginBottom: 12, color: '#666' }}>
              当前第 {assetChangeIndex + 1} / {assetChangeAssets.length} 台资产
            </div>
            <div style={{ marginBottom: 12 }}>
              当前资产：<b>{assetChangeAssets[assetChangeIndex]?.asset_name}</b>
              {assetChangeAssets[assetChangeIndex]?.asset_code ? `（${assetChangeAssets[assetChangeIndex]?.asset_code}）` : ''}
            </div>
            <div style={{ marginBottom: 12, color: '#666' }}>
              当前使用人：{assetChangeAssets[assetChangeIndex]?.user_name || ''}
              {'；'} 当前存放地点：{assetChangeAssets[assetChangeIndex]?.location_name || ''}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>选择新使用人</div>
              <Select
                style={{ width: '100%' }}
                value={assetNewUserId}
                options={assetChangeUsersOpt.map(u => ({ value: u.id, label: u.username }))}
                onChange={v => setAssetNewUserId(v)}
                placeholder="请选择使用人"
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '');
                  return label.includes(input);
                }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>选择新存放地点</div>
              <Select
                style={{ width: '100%' }}
                value={assetNewLocationId}
                options={assetChangeLocsOpt.map((l) => {
                  const regionName = l?.region_name;
                  const showRegion = regionName && regionName !== '未分配区域';
                  return {
                    value: l.id,
                    label: showRegion ? `${l.location_name}（${regionName}）` : l.location_name,
                  };
                })}
                onChange={v => setAssetNewLocationId(v)}
                placeholder="请选择存放地点"
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '');
                  return label.includes(input);
                }}
              />
            </div>
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                loading={assetChangeLoading}
                onClick={applyAssetChangeForCurrent}
              >
                {assetChangeIndex >= assetChangeAssets.length - 1 ? '保存并删除用户' : '保存并下一条'}
              </Button>
              <Button
                onClick={() => {
                  setAssetBatchNewUserId(assetNewUserId);
                  setAssetBatchNewLocationId(assetNewLocationId);
                  setAssetBatchOpen(true);
                }}
                disabled={!assetChangeUsersOpt.length || !assetChangeLocsOpt.length}
              >
                一键变更全部
              </Button>
              <Button danger onClick={() => closeAssetWizard(false)}>
                取消删除
              </Button>
            </Space>
          </>
        ) : (
          <div style={{ color: '#666' }}>未找到该用户的关联资产。</div>
        )}
      </DraggableModal>

      <DraggableModal
        title="一键变更全部资产"
        open={assetBatchOpen}
        onCancel={() => setAssetBatchOpen(false)}
        onOk={applyAssetBatchChangeAndDelete}
        okText="变更并删除"
        okButtonProps={{ danger: true }}
        confirmLoading={assetBatchLoading}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666' }}>
          将把该用户下全部 {assetChangeAssets?.length || 0} 台资产变更为同一使用人和同一存放地点。
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>选择新使用人</div>
          <Select
            style={{ width: '100%' }}
            value={assetBatchNewUserId}
            options={assetChangeUsersOpt.map(u => ({ value: u.id, label: u.username }))}
            onChange={v => setAssetBatchNewUserId(v)}
            placeholder="请选择使用人"
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const label = String(option?.label || '');
              return label.includes(input);
            }}
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>选择新存放地点</div>
          <Select
            style={{ width: '100%' }}
            value={assetBatchNewLocationId}
            options={assetChangeLocsOpt.map((l) => {
              const regionName = l?.region_name;
              const showRegion = regionName && regionName !== '未分配区域';
              return {
                value: l.id,
                label: showRegion ? `${l.location_name}（${regionName}）` : l.location_name,
              };
            })}
            onChange={v => setAssetBatchNewLocationId(v)}
            placeholder="请选择存放地点"
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const label = String(option?.label || '');
              return label.includes(input);
            }}
          />
        </div>
      </DraggableModal>

      <DraggableModal
        title="自定义排序"
        open={sortModalOpen}
        onOk={applySortRules}
        onCancel={() => setSortModalOpen(false)}
        okText="应用排序"
        cancelText="取消"
        width={760}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666' }}>
          拖拽可调整优先级（排序条件1优先级最高），字段不可重复。
        </div>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {draftSortRules.map((rule, idx) => (
            <Space
              key={`draft-sort-${idx}`}
              wrap
              draggable
              onDragStart={() => setDraggingSortIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (draggingSortIndex === null) return;
                const next = [...draftSortRules];
                const [moved] = next.splice(draggingSortIndex, 1);
                next.splice(idx, 0, moved);
                setDraftSortRules(next);
                setDraggingSortIndex(null);
              }}
              onDragEnd={() => setDraggingSortIndex(null)}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: draggingSortIndex === idx ? '1px dashed #1677ff' : '1px solid #f0f0f0',
                borderRadius: 6,
                background: draggingSortIndex === idx ? '#f0f7ff' : '#fff',
              }}
            >
              <HolderOutlined style={{ color: '#999', cursor: 'grab' }} />
              <span style={{ color: '#666' }}>排序条件{idx + 1}</span>
              <Select
                style={{ width: 180 }}
                value={rule.field}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], field: v };
                  setDraftSortRules(next);
                }}
                options={sortFieldOptions.filter(opt => {
                  const used = draftSortRules
                    .map((item, i) => (i === idx ? null : item.field))
                    .filter(Boolean);
                  return !used.includes(opt.value);
                })}
              />
              <Select
                style={{ width: 120 }}
                value={rule.order}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], order: v };
                  setDraftSortRules(next);
                }}
                options={[
                  { value: 'asc', label: '升序' },
                  { value: 'desc', label: '降序' },
                ]}
              />
              <Button
                onClick={() => setDraftSortRules(draftSortRules.filter((_, i) => i !== idx))}
              >
                删除
              </Button>
            </Space>
          ))}
          <Space>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                const used = draftSortRules.map(r => r.field);
                const firstUnused = sortFieldOptions.find(opt => !used.includes(opt.value));
                if (!firstUnused) {
                  message.info('可选排序字段已全部使用');
                  return;
                }
                setDraftSortRules([...draftSortRules, { field: firstUnused.value, order: 'asc' }]);
              }}
              style={{ width: 140 }}
            >
              增加排序条件
            </Button>
            <Button onClick={restoreDefaultSortRules}>
              恢复默认排序
            </Button>
          </Space>
        </Space>
      </DraggableModal>
    </div>
  );
}
